import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  MultiSelect,
  Checkbox,
} from "./ui";
import { ModelBuilderService } from "../services/modelBuilderService";
import { ProjectService } from "../services/projectService";

const AssignmentRuleEditor = ({
  isOpen,
  rule,
  modelBuilders,
  onSave,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    state_filter: [],
    acreage_type: "none", // 'none', 'min', 'max', or 'range'
    acreage_min: "",
    acreage_max: "",
    template_filters: [],
    client_ids: [],
    client_names: [], // For display purposes
    eligible_model_builder_ids: [],
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Available state options
  const stateOptions = [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
  ];

  // Template filter options (02- templates + Linework)
  const templateOptions = [
    "01-Linework Design",
    "02-Commercial",
    "02-Corridor",
    "02-Elevated Utilities",
    "02-Erosion Surface",
    "02-Landfill",
    "02-Multifamily",
    "02-Pond",
    "02-Solar",
    "02-Sports Field",
    "02-Subdivision",
  ];

  // Load clients on mount and sort with active clients at top
  useEffect(() => {
    const loadClients = async () => {
      try {
        const clientsData = await ProjectService.getClients();
        // Sort: Active Client first, then alphabetically by name
        const sortedClients = clientsData.sort((a, b) => {
          const aIsActive = a.client_status === "Active Client";
          const bIsActive = b.client_status === "Active Client";

          if (aIsActive && !bIsActive) return -1;
          if (!aIsActive && bIsActive) return 1;
          return a.name.localeCompare(b.name);
        });
        setClients(sortedClients);
      } catch (err) {
        console.error("Error loading clients:", err);
      }
    };
    loadClients();
  }, []);

  // Initialize form data when rule changes
  useEffect(() => {
    if (rule) {
      // Editing existing rule
      const clientNames =
        rule.client_ids
          ?.map((id) => {
            const client = clients.find((c) => c.id === id);
            return client ? client.name : null;
          })
          .filter(Boolean) || [];

      // Determine acreage type
      let acreageType = "none";
      if (rule.acreage_min && rule.acreage_max) {
        acreageType = "range";
      } else if (rule.acreage_min) {
        acreageType = "min";
      } else if (rule.acreage_max) {
        acreageType = "max";
      }

      setFormData({
        state_filter: rule.state_filter || [],
        acreage_type: acreageType,
        acreage_min: rule.acreage_min || "",
        acreage_max: rule.acreage_max || "",
        template_filters: rule.template_filters || [],
        client_ids: rule.client_ids || [],
        client_names: clientNames,
        eligible_model_builder_ids: rule.eligible_model_builder_ids || [],
      });
    } else {
      // Creating new rule
      setFormData({
        state_filter: [],
        acreage_type: "none",
        acreage_min: "",
        acreage_max: "",
        template_filters: [],
        client_ids: [],
        client_names: [],
        eligible_model_builder_ids: [],
      });
    }
  }, [rule, clients]);

  const handleStateChange = (states) => {
    setFormData({ ...formData, state_filter: states });
  };

  const handleTemplateChange = (templates) => {
    setFormData({ ...formData, template_filters: templates });
  };

  const handleClientChange = (clientNames) => {
    // Map client names back to IDs
    const clientIds = clientNames
      .map((name) => {
        const client = clients.find((c) => c.name === name);
        return client ? client.id : null;
      })
      .filter(Boolean);

    setFormData({
      ...formData,
      client_names: clientNames,
      client_ids: clientIds,
    });
  };

  const handleBuilderToggle = (builderId) => {
    const { eligible_model_builder_ids } = formData;
    const newIds = eligible_model_builder_ids.includes(builderId)
      ? eligible_model_builder_ids.filter((id) => id !== builderId)
      : [...eligible_model_builder_ids, builderId];

    setFormData({ ...formData, eligible_model_builder_ids: newIds });
  };

  const validate = () => {
    const newErrors = {};

    // At least one builder must be selected
    if (formData.eligible_model_builder_ids.length === 0) {
      newErrors.builders = "At least one model builder must be selected";
    }

    // Acreage validation based on type
    if (formData.acreage_type === "min" && !formData.acreage_min) {
      newErrors.acreage = 'Minimum acreage is required when type is "Min"';
    } else if (formData.acreage_type === "max" && !formData.acreage_max) {
      newErrors.acreage = 'Maximum acreage is required when type is "Max"';
    } else if (formData.acreage_type === "range") {
      if (!formData.acreage_min || !formData.acreage_max) {
        newErrors.acreage =
          "Both minimum and maximum acreage are required for range";
      } else {
        const min = parseFloat(formData.acreage_min);
        const max = parseFloat(formData.acreage_max);
        if (max < min) {
          newErrors.acreage =
            "Maximum acreage must be greater than or equal to minimum";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      // Prepare acreage values based on type
      let acreageMin = null;
      let acreageMax = null;
      if (
        formData.acreage_type === "min" ||
        formData.acreage_type === "range"
      ) {
        acreageMin = formData.acreage_min
          ? parseFloat(formData.acreage_min)
          : null;
      }
      if (
        formData.acreage_type === "max" ||
        formData.acreage_type === "range"
      ) {
        acreageMax = formData.acreage_max
          ? parseFloat(formData.acreage_max)
          : null;
      }

      // Prepare data for API
      const apiData = {
        state_filter:
          formData.state_filter.length > 0 ? formData.state_filter : null,
        acreage_min: acreageMin,
        acreage_max: acreageMax,
        template_filters:
          formData.template_filters.length > 0
            ? formData.template_filters
            : null,
        client_ids: formData.client_ids.length > 0 ? formData.client_ids : null,
        client_names: formData.client_names,
        eligible_model_builder_ids: formData.eligible_model_builder_ids,
      };

      if (rule) {
        // Update existing rule
        await ModelBuilderService.updateAssignmentRule(rule.id, apiData);
      } else {
        // Create new rule
        await ModelBuilderService.createAssignmentRule(apiData);
      }

      onSave();
    } catch (err) {
      console.error("Error saving rule:", err);
      alert("Failed to save rule. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Get builder color
  const getBuilderColor = (name) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes("adam")) return "#3b82f6";
    if (lowerName.includes("zach")) return "#8b5cf6";
    if (lowerName.includes("stef")) return "#f59e0b";
    return "#6b7280";
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} size="lg">
      <ModalHeader>
        {rule ? "Edit Assignment Rule" : "Create Assignment Rule"}
      </ModalHeader>

      <ModalBody>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-4)",
          }}
        >
          {/* State Filter */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-medium)",
                color: "var(--gray-300)",
                marginBottom: "var(--space-2)",
              }}
            >
              State Filter (optional)
            </label>
            <MultiSelect
              value={formData.state_filter}
              options={stateOptions}
              onChange={handleStateChange}
              placeholder="Select states..."
            />
            <p
              style={{
                fontSize: "var(--text-xs)",
                color: "var(--gray-500)",
                marginTop: "var(--space-1)",
              }}
            >
              Leave empty to match all states
            </p>
          </div>

          {/* Acreage Filter */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-medium)",
                color: "var(--gray-300)",
                marginBottom: "var(--space-2)",
              }}
            >
              Acreage Filter (optional)
            </label>

            {/* Acreage Type Selector */}
            <div
              style={{
                display: "flex",
                gap: "var(--space-3)",
                marginBottom: "var(--space-2)",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-1)",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="acreage_type"
                  value="none"
                  checked={formData.acreage_type === "none"}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      acreage_type: e.target.value,
                      acreage_min: "",
                      acreage_max: "",
                    })
                  }
                />
                <span
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--gray-300)",
                  }}
                >
                  None
                </span>
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-1)",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="acreage_type"
                  value="min"
                  checked={formData.acreage_type === "min"}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      acreage_type: e.target.value,
                      acreage_max: "",
                    })
                  }
                />
                <span
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--gray-300)",
                  }}
                >
                  Min
                </span>
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-1)",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="acreage_type"
                  value="max"
                  checked={formData.acreage_type === "max"}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      acreage_type: e.target.value,
                      acreage_min: "",
                    })
                  }
                />
                <span
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--gray-300)",
                  }}
                >
                  Max
                </span>
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-1)",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="acreage_type"
                  value="range"
                  checked={formData.acreage_type === "range"}
                  onChange={(e) =>
                    setFormData({ ...formData, acreage_type: e.target.value })
                  }
                />
                <span
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--gray-300)",
                  }}
                >
                  Range
                </span>
              </label>
            </div>

            {/* Acreage Input Fields */}
            {formData.acreage_type !== "none" && (
              <div
                style={{
                  display: "flex",
                  gap: "var(--space-2)",
                  alignItems: "center",
                }}
              >
                {(formData.acreage_type === "min" ||
                  formData.acreage_type === "range") && (
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.acreage_min}
                    onChange={(e) =>
                      setFormData({ ...formData, acreage_min: e.target.value })
                    }
                    placeholder="Minimum acres"
                  />
                )}
                {formData.acreage_type === "range" && (
                  <span style={{ color: "var(--gray-400)" }}>to</span>
                )}
                {(formData.acreage_type === "max" ||
                  formData.acreage_type === "range") && (
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.acreage_max}
                    onChange={(e) =>
                      setFormData({ ...formData, acreage_max: e.target.value })
                    }
                    placeholder="Maximum acres"
                  />
                )}
              </div>
            )}

            {errors.acreage && (
              <p
                style={{
                  fontSize: "var(--text-xs)",
                  color: "var(--danger)",
                  marginTop: "var(--space-1)",
                }}
              >
                {errors.acreage}
              </p>
            )}
          </div>

          {/* Template Filters */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-medium)",
                color: "var(--gray-300)",
                marginBottom: "var(--space-2)",
              }}
            >
              Template Filters (optional)
            </label>
            <MultiSelect
              value={formData.template_filters}
              options={templateOptions}
              onChange={handleTemplateChange}
              placeholder="Select templates..."
            />
            <p
              style={{
                fontSize: "var(--text-xs)",
                color: "var(--gray-500)",
                marginTop: "var(--space-1)",
              }}
            >
              Projects must have at least one matching template
            </p>
          </div>

          {/* Client Filter */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-medium)",
                color: "var(--gray-300)",
                marginBottom: "var(--space-2)",
              }}
            >
              Client Filter (optional)
            </label>
            <MultiSelect
              value={formData.client_names}
              options={clients.map((c) => c.name)}
              onChange={handleClientChange}
              placeholder="Select clients..."
            />
          </div>

          {/* Eligible Model Builders */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-medium)",
                color: "var(--gray-300)",
                marginBottom: "var(--space-2)",
              }}
            >
              Eligible Model Builders *
            </label>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--space-2)",
              }}
            >
              {modelBuilders.map((builder) => (
                <label
                  key={builder.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-2)",
                    padding: "var(--space-2)",
                    backgroundColor: "var(--dark-elevated)",
                    borderRadius: "var(--radius-md)",
                    border: formData.eligible_model_builder_ids.includes(
                      builder.id,
                    )
                      ? `2px solid ${getBuilderColor(builder.name)}`
                      : "2px solid var(--gray-700)",
                    cursor: "pointer",
                    transition: "all var(--transition-fast)",
                  }}
                  onMouseEnter={(e) => {
                    if (
                      !formData.eligible_model_builder_ids.includes(builder.id)
                    ) {
                      e.currentTarget.style.borderColor = "var(--gray-600)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      !formData.eligible_model_builder_ids.includes(builder.id)
                    ) {
                      e.currentTarget.style.borderColor = "var(--gray-700)";
                    }
                  }}
                >
                  <Checkbox
                    checked={formData.eligible_model_builder_ids.includes(
                      builder.id,
                    )}
                    onChange={() => handleBuilderToggle(builder.id)}
                  />
                  <span
                    style={{
                      fontSize: "var(--text-sm)",
                      fontWeight: "var(--font-medium)",
                      color: formData.eligible_model_builder_ids.includes(
                        builder.id,
                      )
                        ? getBuilderColor(builder.name)
                        : "var(--gray-300)",
                    }}
                  >
                    {builder.name}
                  </span>
                </label>
              ))}
            </div>
            {errors.builders && (
              <p
                style={{
                  fontSize: "var(--text-xs)",
                  color: "var(--danger)",
                  marginTop: "var(--space-1)",
                }}
              >
                {errors.builders}
              </p>
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button onClick={onCancel} variant="secondary" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="primary" disabled={loading}>
          {loading ? "Saving..." : rule ? "Update Rule" : "Create Rule"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AssignmentRuleEditor;
